import { Button, Form, Input, Select } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "antd/es/form/Form";
import SearchIcon from "../../../Assets/svg/searchIcon.svg";
import { debounce } from "lodash";
import "./publicMap.scss";
import axios from "axios";

const baseUrl = process.env.REACT_APP_API_BASE_URL.endsWith("/")
  ? process.env.REACT_APP_API_BASE_URL
  : `${process.env.REACT_APP_API_BASE_URL}/`;

const { Option } = Select;

const statusLabels = {
  2: "Pre-planning phase",
  3: "Planning",
  4: "Data Collection",
  5: "Post data collection/Data check",
  6: "Visit completed",
};

const availableStatuses = [2, 3, 4, 5, 6];

const Filters = ({ setFilters, initialFilters }) => {
  const [form] = useForm();
  const [showFilters, setShowFilters] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]); // New state for states
  const [facilities, setFacilities] = useState([]);

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        Search: value,
      }));
    }, 1000),
    []
  );

  const handleSearch = (event) => {
    const value = event?.target.value.toLowerCase();
    debouncedSearchHandler(value);
  };

  useEffect(() => {
    getCountriesOfSiteVisits();
    getFacilities();
  }, []);

  const getCountriesOfSiteVisits = () => {
    axios
      .get(`${baseUrl}api/Site/getCountriesOfSiteVisits`)
      .then((res) => {
        const data = res.data.data;
        const formatedData = data
          .filter(
            (country) =>
              country.trim() !== "string" && country.trim() !== "undefined"
          )
          .map((country, index) => ({
            id: index,
            stateName: country.trim(),
          }));
        setCountries(formatedData);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  const getStatesOfSiteVisits = (country) => {
    if (!country) {
      setStates([]);
      setFilters((prev) => ({ ...prev, State: undefined }));
      return;
    }

    axios
      .get(`${baseUrl}api/Site/getStatesOfSiteVisits`, {
        params: { country },
      })
      .then((res) => {
        const data = res.data.data;
        const formattedStates = data
          .filter(
            (state) => state.trim() !== "string" && state.trim() !== "undefined"
          )
          .map((state, index) => ({
            id: index,
            stateName: state.trim(),
          }));
        setStates(formattedStates);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  const getFacilities = () => {
    axios
      .get(`${baseUrl}api/Facility`)
      .then((res) => {
        const data = res.data.data;
        setFacilities(data);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  const clearAll = () => {
    setFilters(initialFilters);
    form.setFieldsValue({
      Search: "",
      Country: undefined,
      State: undefined, // Clear state selection too
      SiteVisitStatus: [],
      FacilityType: [],
    });
    setStates([]);
  };

  return (
    <>
      <Form form={form} layout="vertical" className="filtersWrapper mb-5">
        <div className="d-flex flex-row flex-wrap justify-content-between ms-1 w-100">
          <div className="flex-grow-1 me-3">
            <Form.Item name="Search" className="m-0">
              <Input
                placeholder="Search by site visit name..."
                onChange={handleSearch}
                suffix={<img src={SearchIcon} alt="search icon" />}
              />
            </Form.Item>
          </div>
          <div className="d-flex flex-row gap-3">
            <Button className="clearButton" onClick={clearAll}>
              Clear All
            </Button>
            <Button onClick={handleToggleFilters} className="toggleButton">
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        </div>
        {showFilters && (
          <div className="d-flex flex-row flex-wrap gap-5 w-100 mt-3">
            <Form.Item
              name="Country"
              label="Country"
              className="m-0"
              style={{ width: "300px" }}
            >
              <Select
                placeholder="Select Country"
                showSearch
                allowClear
                onChange={(value) => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    Country: value,
                    State: undefined, // Reset State filter when country changes
                  }));
                  getStatesOfSiteVisits(value);
                  form.setFieldsValue({ State: undefined });
                }}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) ===
                  0
                }
              >
                {countries.map((country) => (
                  <Option key={country?.id} value={country?.stateName}>
                    {country.stateName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="State"
              label="Governorate/State"
              className="m-0"
              style={{ width: "300px" }}
            >
              <Select
                placeholder="Select Governorate/State"
                allowClear
                showSearch
                onChange={(value) =>
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    State: value,
                  }))
                }
                disabled={states.length === 0}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) ===
                  0
                }
              >
                {states.map((state) => (
                  <Option key={state?.id} value={state?.stateName}>
                    {state.stateName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="SiteVisitStatus"
              label="Site Visit Status"
              className="m-0"
              style={{ width: "300px" }}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="Select status"
                onChange={(value) =>
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    SiteVisitStatus: value,
                  }))
                }
                style={{ width: "100%" }}
              >
                {availableStatuses?.map((status) => (
                  <Option key={status} value={status}>
                    {statusLabels[status] || status}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="FacilityType"
              label="Facility Type"
              className="m-0"
              style={{ width: "300px" }}
            >
              <Select
                allowClear
                mode="multiple"
                placeholder="Select facility"
                onChange={(value) =>
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    FacilityIds: value,
                  }))
                }
                style={{ width: "100%" }}
              >
                {facilities?.map((facility) => (
                  <Option key={facility?.id} value={facility?.id}>
                    {facility?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}
      </Form>
    </>
  );
};

export default Filters;
